/**
 * Transcript projection (RFC 0215: Log substrate).
 *
 * Derives the linear LLM conversation from the entry DAG. Coordination entries
 * (run, inference_step, worker_migration, object_*, decision) do not appear in
 * the transcript; worker_migration surfaces as a synthetic context note.
 */

import { COMMENT_ANSWER_RULE } from './comment-answer-rule'
import { ancestorClosure } from './dag'
import { topoOrder } from './projection-order'
import { projectStopReports } from './projection-stops'
import type {
  AttachmentRef,
  EntryId,
  LogDoc,
  MessageData,
  RunStatus,
  ToolUseId,
} from './types'
import {
  COMMENT_CLAIMED_EXTENSION,
  COMMENT_REPLIED_EXTENSION,
  EDITED_EXTENSION,
  INTERACTION_ANSWERED_EXTENSION,
  INTERACTION_REQUESTED_EXTENSION,
  MENTIONED_EXTENSION,
  PAGE_HANDED_BACK_EXTENSION,
  PAGE_TAKEN_OVER_EXTENSION,
  SCHEDULE_DUE_EXTENSION,
  SESSION_REPLIED_EXTENSION,
  SESSION_STOPPED_EXTENSION,
  UNDONE_EXTENSION,
  VOICE_RELAYED_EXTENSION,
} from './types'
import { readWrittenOn, type WrittenOn } from './writes'

/**
 * What a comment notification says it is ABOUT.
 *
 * Both halves when there are two. `anchorText` is the only part a person reads
 * and `nodeId` is the only part that can be filtered on, so a wake carrying one
 * without the other leaves the agent either unable to say what was meant or
 * unable to go and look. On a canvas there is no quotable prose at all, which
 * is the case that produced remarks reading "remove this label" with nothing in
 * them to say which label.
 */
function aboutClause(anchorText: unknown, nodeId: unknown): string {
  const text = String(anchorText ?? '')
  const node = String(nodeId ?? '')
  if (text && node) return `, about: "${text}" (nodeId ${node})`
  if (text) return `, about: "${text}"`
  if (node) return `, about nodeId ${node}`
  return ''
}

/**
 * The target to LIST with, spelled out, when the comment names one object.
 *
 * Without it the instruction is "read the thread" on a document that may hold
 * fifty of them, and the agent reads all fifty to find the one it was woken
 * for. Omitted when there is no node, where a bare `action:list` is already the
 * right call.
 */
function listHint(documentId: unknown, nodeId: unknown): string {
  const node = String(nodeId ?? '')
  const doc = String(documentId ?? '')
  if (!node || !doc) return ''
  return ` target:{"documentId":"${doc}","nodeId":"${node}"}`
}

export type TranscriptRole = 'user' | 'assistant' | 'tool_result' | 'system'

export interface TranscriptText {
  type: 'text'
  text: string
}
export interface TranscriptToolUse {
  type: 'tool_use'
  id: ToolUseId
  name: string
  arguments: unknown
}
/**
 * RFC 0288: a file that came with the message. The bytes are one document away
 * — this block is the pointer, and resolving it is the renderer's job.
 */
export interface TranscriptAttachment extends AttachmentRef {
  type: 'attachment'
}
/** Visual context captured by the shell. Models receive it; threads do not
 * render it as a file the person deliberately attached. */
export interface TranscriptViewAttachment extends AttachmentRef {
  type: 'view_attachment'
}
export type TranscriptBlock =
  | TranscriptText
  | TranscriptToolUse
  | TranscriptAttachment
  | TranscriptViewAttachment

/** RFC 0307 3c: a line said on a voice call. */
export interface TranscriptSpoken {
  callId: string
  by: 'person' | 'voice'
  text: string
  /** The voice sent this line to the agent: a run was queued on it. */
  relayed: boolean
  /** A line in another session links to the session holding the whole call. */
  homeSessionId?: string
  homeEntryId?: EntryId
  /** At home, the addressed session that received this line. */
  targetSessionId?: string
}

export interface TranscriptMessage {
  role: TranscriptRole
  content: TranscriptBlock[]
  /** The log entry this row came from. Stable across causal reordering. */
  entryId?: EntryId
  /** When that entry was written, in millis (RFC 0313 3h: a session's
   *  Changes reads its file writes in time order beside document changes). */
  at?: number
  /** RFC 0345: the person who wrote a user message (hex id), when the log
   *  names one: a shared session's members are told apart by it. */
  by?: string
  /** The run answering this prompt, while it has one. */
  runStatus?: RunStatus
  /**
   * Whether that run took a step. A prompt whose run never started stays where
   * it was posted, inside an earlier turn, so it does not end that turn.
   * Present whenever `runStatus` is.
   */
  runStarted?: boolean
  /**
   * RFC 0348 3c: a stop report the World agent still owes a decision. Set on
   * `session_stopped` rows only: true until a `decide_stop` covers it, absent
   * on a start or a stop the person had open, which are owed nothing. A
   * harness prompt reads every owed report, wherever on the log it landed.
   */
  owed?: boolean
  /** Set on tool_result messages: the tool_use block this answers. */
  toolUseId?: ToolUseId
  isError?: boolean
  /**
   * What SYNTHESISED this system message, when it is not something anyone said
   * (RFC 0286 §5a). `session_reply` is a session reporting back on work it was
   * dispatched; `comment_reply` is a person answering a comment this session
   * wrote (RFC 0289); `mentioned` is somebody naming this session in a body of
   * prose (RFC 0292). A host that has to turn a transcript into ONE prompt
   * (the harness seam) reads this to tell "a new thing to respond to" from
   * "context about the conversation so far".
   */
  kind?:
    | 'session_reply'
    | 'session_stopped'
    | 'schedule_due'
    | 'comment_reply'
    | 'mentioned'
    | 'comment_claimed'
    | 'edited'
    | 'undone'
    | 'page_taken_over'
    | 'page_handed_back'
    | 'interaction_answer'
    | 'voice_call'
  /**
   * RFC 0307 3c: this row is a line said on a voice call. `text` is the words
   * as said; `content` is what a model reads, which for a line nobody relayed
   * says so, so the agent takes it as context and not as an instruction.
   * `relayed` is derived, not stored: a person's line is relayed exactly when
   * a run was queued on it.
   */
  spoken?: TranscriptSpoken
  /**
   * What the synthesised message is ABOUT, beside the prose that says it.
   *
   * Set only on the three `kind`s above, and kept beside the text for RFC 0292's
   * reason: the sentence is written for a model to read, and a surface that
   * wants to draw a card with somewhere to CLICK would otherwise have to parse
   * English back into the ids the event already carried. Additive — a reader
   * that ignores `ref` renders exactly the text it rendered before.
   */
  ref?: TranscriptRef
  /**
   * On a write's tool_result: where the file landed (RFC 0329 3a), the branch
   * the holding checkout had out and the path inside it. Absent for any other
   * result, and for a write nobody placed.
   */
  writtenOn?: WrittenOn
}

/** The ids a synthesised system message was built from (RFC 0286/0289/0292). */
export interface TranscriptRef {
  /** On a stop report (RFC 0348): how the session stopped, `completed`,
   *  `failed`, `cancelled` or `asking`. */
  status?: string
  /** The session that replied. Absent on a comment event, whose author is a
   *  person or an agent id rather than a session anyone can open. */
  sessionId?: string
  /** The comment to answer — the PARENT for a reply, the comment itself for a
   *  mention. The same id the prose tells the agent to pass to `comment`. */
  commentId?: string
  /** On a reply: the reply itself, which is what the person just wrote and
   *  what a pass marks read. `commentId` is then the comment it answered. */
  replyId?: string
  /** The document the comment sits on, when it sits on one. */
  documentId?: string
  /** Who wrote it, as the log recorded them. */
  author?: string
  /** The exact text the comment is about, when it quoted any. */
  anchorText?: string
  /** The one line of the body that rode along with the event. */
  summary?: string
}

/** Drop the fields the event did not carry, so `ref` never invents an id. A
 *  card keyed on a present-but-undefined `documentId` would offer to open
 *  nothing. */
function refOf(fields: Record<string, unknown>): TranscriptRef {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(fields)) {
    // A scalar read off the log is an ImmutableString, not a string, so
    // testing `typeof` alone dropped every id and every card lost its link.
    const text =
      typeof v === 'string'
        ? v
        : typeof (v as { val?: unknown } | null)?.val === 'string'
          ? (v as { val: string }).val
          : ''
    if (text.length > 0) out[k] = text
  }
  return out as TranscriptRef
}

/** RFC 0329 3a. Only when the result carries a record. */
function writtenOnOf(m: MessageData): { writtenOn?: WrittenOn } {
  const writtenOn = readWrittenOn(m.metadata)
  return writtenOn ? { writtenOn } : {}
}

/**
 * A line said on a voice call (RFC 0307 3c).
 *
 * A person's line the voice relayed is the prompt of the run queued on it, so
 * it is a `user` message in the person's own words. Every other line, the
 * voice's and the person's unrelayed ones, is context: a `system` row whose
 * text says what it was, so an agent reading the transcript does not take
 * "hmm, let me think" as something to act on, or the voice's words as its own.
 */
function spokenRow(
  m: MessageData,
  meta: { entryId: EntryId; at?: number; runStatus?: RunStatus },
  hasRun: boolean,
  targetSessionId?: string,
): TranscriptMessage {
  const by: 'person' | 'voice' =
    String(m.spokenBy ?? '') === 'voice' ? 'voice' : 'person'
  const text = m.text ? String(m.text) : ''
  const relayed = by === 'person' && (hasRun || !!targetSessionId)
  const spoken: TranscriptSpoken = {
    callId: String(m.voiceCallId),
    by,
    text,
    relayed,
    ...(m.voiceHomeSessionId
      ? {
          homeSessionId: String(m.voiceHomeSessionId),
          homeEntryId: String(m.voiceHomeEntryId ?? ''),
        }
      : {}),
    ...(targetSessionId ? { targetSessionId } : {}),
  }
  if (hasRun) {
    return { ...meta, role: 'user', content: textBlocks(m), spoken }
  }
  return {
    ...meta,
    role: 'system',
    kind: 'voice_call',
    content: [
      {
        type: 'text',
        text:
          by === 'voice'
            ? `On the voice call, the voice assistant said: ${text}`
            : targetSessionId
              ? `On the voice call, the person said (sent to session ${targetSessionId}, not an instruction to you): ${text}`
              : `On the voice call, the person said (not sent to you as an instruction): ${text}`,
      },
    ],
    spoken,
  }
}

function textBlocks(m: MessageData): TranscriptBlock[] {
  return m.text && m.text.length > 0 ? [{ type: 'text', text: m.text }] : []
}

/** RFC 0288. Ordered AFTER the text, matching how the composer stacks them. */
function attachmentBlocks(m: MessageData): TranscriptBlock[] {
  return (m.attachments ?? []).map((a) => ({ type: 'attachment', ...a }))
}

/** RFC 0304 3d. Ordered after explicit attachments, but hidden by transcript
 * renderers because the screen is turn context rather than message content. */
function viewAttachmentBlocks(m: MessageData): TranscriptBlock[] {
  return (m.viewAttachments ?? []).map((a) => ({
    type: 'view_attachment',
    ...a,
  }))
}

function toolUseBlocks(m: MessageData): TranscriptBlock[] {
  return m.toolUses.map((b) => ({
    type: 'tool_use',
    id: b.id,
    name: b.name,
    arguments: b.arguments,
  }))
}

/** Project the log into a linear LLM transcript. */
export function projectTranscript(doc: LogDoc): TranscriptMessage[] {
  return projectTranscriptEntries(doc)
}

/**
 * Project only the causal past visible from `heads` (RFC 0302 S2).
 * Sibling posts, answers, and newer global heads are ineligible by construction.
 */
export function projectTranscriptFromHeads(
  doc: LogDoc,
  heads: readonly EntryId[],
): TranscriptMessage[] {
  return projectTranscriptEntries(doc, ancestorClosure(doc, heads))
}

/**
 * Provider context for one run. A continue may have been posted while the
 * preceding answer was still streaming. It is durable immediately, but the
 * provider must read it AFTER the answer it waited for, not at its wall-clock
 * insertion point.
 */
export function projectTranscriptForRun(
  doc: LogDoc,
  heads: readonly EntryId[],
  promptMessageId: EntryId,
): TranscriptMessage[] {
  const eligible = ancestorClosure(doc, heads)
  return projectTranscriptEntries(
    doc,
    eligible,
    waitedForAssistant(doc, eligible, promptMessageId)
      ? promptMessageId
      : undefined,
  )
}

/** Whether this prompt waited for an answer that was not visible when sent. */
function waitedForAssistant(
  doc: LogDoc,
  eligible: ReadonlySet<EntryId>,
  promptMessageId: EntryId,
): boolean {
  const promptPast = ancestorClosure(doc, [promptMessageId])
  for (const id of eligible) {
    if (promptPast.has(id)) continue
    const payload = doc.entries[id]?.payload
    if (payload?.type === 'message' && payload.payload.role === 'assistant') {
      return true
    }
  }
  return false
}

function projectionOrder(
  doc: LogDoc,
  eligible?: ReadonlySet<EntryId>,
  promptLast?: EntryId,
) {
  const ordered = topoOrder(doc).filter((e) => !eligible || eligible.has(e.id))

  // Once a continued run starts, its prompt settles immediately before that
  // run in the visible conversation. Until then it stays where it was posted
  // and carries a queued label, so sending always has immediate feedback.
  const promptByRun = new Map<EntryId, EntryId>()
  for (const entry of ordered) {
    if (entry.payload.type !== 'run') continue
    const run = entry.payload.payload
    if (run.branch !== true) promptByRun.set(entry.id, run.promptMessageId)
  }
  const moved = new Set<EntryId>()
  for (const step of [...ordered]) {
    if (step.payload.type !== 'inference_step') continue
    const prompt = promptByRun.get(step.payload.payload.runId)
    if (!prompt || moved.has(prompt)) continue
    const stepPast = ancestorClosure(doc, step.payload.payload.inputHeads)
    if (!waitedForAssistant(doc, stepPast, prompt)) {
      moved.add(prompt)
      continue
    }
    const from = ordered.findIndex((entry) => entry.id === prompt)
    const before = ordered.findIndex((entry) => entry.id === step.id)
    if (from < 0 || before < 0) continue
    if (from !== before - 1) {
      const [entry] = ordered.splice(from, 1)
      if (!entry) continue
      const destination = ordered.findIndex(
        (candidate) => candidate.id === step.id,
      )
      if (destination < 0) continue
      ordered.splice(destination, 0, entry)
    }
    moved.add(prompt)
  }

  if (promptLast) {
    const from = ordered.findIndex((entry) => entry.id === promptLast)
    if (from >= 0) ordered.push(...ordered.splice(from, 1))
  }
  return ordered
}

interface TaskQuestion {
  taskId: string
  questions: Array<{
    id: string
    prompt: string
    options: Array<{ value: string; label: string }>
  }>
}

/** Questions that were posted on a task, by interaction id. */
function taskQuestionsById(doc: LogDoc): Map<string, TaskQuestion> {
  const out = new Map<string, TaskQuestion>()
  for (const entry of Object.values(doc.entries ?? {})) {
    const p = entry.payload
    if (
      p.type !== 'extension' ||
      p.payload.extensionType !== INTERACTION_REQUESTED_EXTENSION
    )
      continue
    const d = p.payload.payload as {
      interactionId?: unknown
      bodyJson?: unknown
    }
    try {
      const body = JSON.parse(String(d.bodyJson ?? '')) as Partial<TaskQuestion>
      if (typeof body.taskId === 'string' && body.taskId) {
        out.set(String(d.interactionId ?? ''), {
          taskId: body.taskId,
          questions: Array.isArray(body.questions) ? body.questions : [],
        })
      }
    } catch {
      // Not JSON: not a task question.
    }
  }
  return out
}

/** The answer as words: each picked option's label, or what they typed. */
function answerWords(asked: TaskQuestion, answerJson: string): string {
  let answers: Record<string, { selected?: string[]; freeText?: string }> = {}
  try {
    answers =
      (JSON.parse(answerJson) as { answers?: typeof answers }).answers ?? {}
  } catch {
    return answerJson
  }
  const parts: string[] = []
  for (const question of asked.questions) {
    const a = answers[question.id]
    if (!a) continue
    const picked = (a.selected ?? []).map(
      (value) =>
        question.options.find((o) => o.value === value)?.label ?? value,
    )
    const said = [
      ...picked,
      ...(a.freeText?.trim() ? [`"${a.freeText.trim()}"`] : []),
    ]
    if (said.length > 0) parts.push(said.join(', '))
  }
  return parts.length > 0 ? parts.join('; ') : 'no answer'
}

function projectTranscriptEntries(
  doc: LogDoc,
  eligible?: ReadonlySet<EntryId>,
  promptLast?: EntryId,
): TranscriptMessage[] {
  const out: TranscriptMessage[] = []
  const runByPrompt = new Map<EntryId, { id: EntryId; status: RunStatus }>()
  const steppedRuns = new Set<EntryId>()
  const addressed = new Map<EntryId, string>()
  let hasReports = false
  const scheduleDueIds = new Set<EntryId>()
  const consumedScheduleDue = new Set<EntryId>()
  for (const entry of Object.values(doc.entries ?? {})) {
    if (
      entry.payload.type === 'extension' &&
      entry.payload.payload.extensionType === SCHEDULE_DUE_EXTENSION
    )
      scheduleDueIds.add(entry.id)
    if (
      entry.payload.type === 'extension' &&
      entry.payload.payload.extensionType === SESSION_STOPPED_EXTENSION
    )
      hasReports = true
    if (entry.payload.type === 'run')
      runByPrompt.set(entry.payload.payload.promptMessageId, {
        id: entry.id,
        status: entry.payload.payload.status,
      })
    if (entry.payload.type === 'inference_step')
      steppedRuns.add(entry.payload.payload.runId)
    if (
      entry.payload.type === 'extension' &&
      entry.payload.payload.extensionType === VOICE_RELAYED_EXTENSION
    ) {
      const p = entry.payload.payload.payload
      addressed.set(String(p.sourceEntryId), String(p.targetSessionId))
    }
  }

  // A reminder is consumed by the turn whose actual inference input contains
  // it. Wall-clock order alone loses one that arrived while a turn was running.
  if (scheduleDueIds.size) {
    const inputs: EntryId[] = []
    for (const entry of Object.values(doc.entries ?? {})) {
      if (entry.payload.type === 'inference_step') {
        inputs.push(...entry.payload.payload.inputHeads.map(String))
      }
    }
    const visible = ancestorClosure(doc, inputs)
    for (const id of scheduleDueIds) {
      if (visible.has(id)) consumedScheduleDue.add(id)
    }
  }

  const taskQuestions = taskQuestionsById(doc)
  // Only the World agent's log has reports; every other session skips the
  // second ordering pass this costs.
  const reports = hasReports ? projectStopReports(doc) : []
  // A stop heard twice draws once, at its latest hearing.
  const shownReports = new Set(reports.map((r) => r.entryId))
  const owed = new Set(
    reports
      .filter((r) => !r.verdict && !r.seen && r.status !== 'started')
      .map((r) => r.entryId),
  )
  for (const e of projectionOrder(doc, eligible, promptLast)) {
    const p = e.payload
    if (p.type === 'message') {
      const m = p.payload
      const run = runByPrompt.get(e.id)
      const meta = {
        entryId: e.id,
        ...(Number.isFinite(Number(e.createdAt))
          ? { at: Number(e.createdAt) }
          : {}),
        ...(run
          ? { runStatus: run.status, runStarted: steppedRuns.has(run.id) }
          : {}),
      }
      if (m.voiceCallId) {
        out.push(spokenRow(m, meta, run !== undefined, addressed.get(e.id)))
        continue
      }
      switch (m.role) {
        case 'tool_result':
          out.push({
            ...meta,
            role: 'tool_result',
            content: textBlocks(m),
            toolUseId: m.toolUseId,
            isError: m.isError,
            ...writtenOnOf(m),
          })
          break
        case 'assistant':
          out.push({
            ...meta,
            role: 'assistant',
            content: [...textBlocks(m), ...toolUseBlocks(m)],
          })
          break
        case 'user':
        case 'system':
          out.push({
            ...meta,
            role: m.role,
            // RFC 0345: in a shared session, who said it (a person's id).
            ...(m.role === 'user' && m.by ? { by: String(m.by) } : {}),
            content: [
              ...textBlocks(m),
              ...attachmentBlocks(m),
              ...viewAttachmentBlocks(m),
              ...toolUseBlocks(m),
            ],
          })
          break
        case 'custom':
          // Extension-provided context projected as a system message.
          out.push({ ...meta, role: 'system', content: textBlocks(m) })
          break
      }
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === INTERACTION_ANSWERED_EXTENSION
    ) {
      // Only a question posted on a task gets a row (RFC 0304 3b): its asker
      // did not wait, so this row is how the answer reaches the next turn. A
      // question that waited already got its answer as the tool's result, and
      // a second copy here reads as the person saying it twice.
      const d = p.payload.payload as {
        interactionId?: unknown
        outcome?: unknown
        answerJson?: unknown
        reason?: unknown
      }
      const asked = taskQuestions.get(String(d.interactionId ?? ''))
      if (asked) {
        out.push({
          role: 'system',
          kind: 'interaction_answer',
          content: [
            {
              type: 'text',
              text:
                String(d.outcome) === 'answered'
                  ? `The person answered your question about task ${asked.taskId}: ${answerWords(asked, String(d.answerJson ?? ''))}. Carry on with the task.`
                  : `The person dismissed your question about task ${asked.taskId}. Use your best judgment and carry on.`,
            },
          ],
        })
      }
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === SESSION_REPLIED_EXTENSION
    ) {
      // RFC 0286 §5a. The reply is an EVENT on the log, and it stays one here:
      // projected as SYSTEM, never as user, because a model reading it as user
      // text would act on "the essay session answered" as an instruction from
      // the person. Framed so the agent knows what it is looking at, and told
      // where the rest of the answer is rather than being handed a copy of it.
      const d = p.payload.payload as {
        sessionId?: unknown
        dispatchId?: unknown
        summary?: unknown
      }
      out.push({
        role: 'system',
        kind: 'session_reply',
        ref: refOf({ sessionId: d.sessionId, summary: d.summary }),
        content: [
          {
            type: 'text',
            text:
              `Session ${String(d.sessionId ?? '?')} has finished the turn you ` +
              `dispatched (dispatch ${String(d.dispatchId ?? '?')}). It said: ` +
              `${String(d.summary ?? '')}\n\n` +
              'That is the first line only. Read the whole reply with ' +
              'read_session on that session id.',
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === SCHEDULE_DUE_EXTENSION
    ) {
      // RFC 0350 3d. SYSTEM, like every event: a reminder is the person's
      // earlier ask arriving on time, not the person speaking now. The agent
      // that wakes decides what to do; a quiet subscriber is only told.
      const d = p.payload.payload as {
        scheduleId?: unknown
        dueAt?: unknown
        late?: unknown
        text?: unknown
        wake?: unknown
      }
      const when = String(d.dueAt ?? '')
      out.push({
        role: 'system',
        // Only the woken agent's copy is something to answer. A quiet copy is
        // context: kinded, a harness would answer it in place of whatever the
        // person said just before it.
        ...(d.wake
          ? {
              kind: 'schedule_due' as const,
              owed: !consumedScheduleDue.has(e.id),
            }
          : {}),
        ref: refOf({ summary: d.text }),
        content: [
          {
            type: 'text',
            text:
              `A reminder the person set came due at ${when}` +
              (d.late ? ' (delivered late: no clock was awake then)' : '') +
              `. It says: ${String(d.text ?? '')}\n\n` +
              (d.wake
                ? 'You are the one it woke. Decide what it needs: tell the ' +
                  'person, ask them something, carry on with the work here, or ' +
                  'start a session in the right project when it needs its own ' +
                  'transcript. '
                : 'Another agent was woken for it; this is so you know. ') +
              `Schedule ${String(d.scheduleId ?? '?')}: list_reminders shows ` +
              'it, change_reminder pauses or cancels it.',
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === SESSION_STOPPED_EXTENSION
    ) {
      if (!shownReports.has(String(e.id))) continue
      // RFC 0348 3a. SYSTEM for the reason a dispatch reply is: an agent
      // stopping somewhere is news, not an instruction from the person.
      const d = p.payload.payload as {
        sessionId?: unknown
        runId?: unknown
        status?: unknown
        interactionId?: unknown
        summary?: unknown
        answeredInARow?: unknown
        seen?: unknown
      }
      const status = String(d.status ?? '')
      const run = String(d.runId ?? '')
      // RFC 0348 3b: the person was reading it, so it is context, not a call.
      const seen =
        status === 'started'
          ? '\n\nA start needs nothing from you: do not decide it or message ' +
            'it. It will tell you again when it stops.'
          : d.seen
            ? '\n\nThe person had this session open when it stopped, so it is ' +
              'theirs already. Do not decide it or message it; this is so you know.'
            : ''
      const twice =
        Number(d.answeredInARow ?? 0) >= 2
          ? '\n\nYou have answered this session twice in a row without the ' +
            'person. Do not message it again: it is theirs (decide_stop "yours").'
          : ''
      const how =
        status === 'started'
          ? 'started a turn'
          : status === 'asking'
            ? 'is waiting on a question'
            : status === 'failed'
              ? 'failed'
              : status === 'cancelled'
                ? 'was stopped'
                : 'finished a turn'
      out.push({
        role: 'system',
        kind: 'session_stopped',
        entryId: e.id,
        ...(owed.has(String(e.id)) ? { owed: true } : {}),
        ref: refOf({
          sessionId: d.sessionId,
          summary: d.summary,
          status: d.status,
        }),
        content: [
          {
            type: 'text',
            text:
              `Session ${String(d.sessionId ?? '?')} ${how}` +
              `${run ? ` (run ${run})` : ''}: ` +
              `${String(d.summary ?? '')}\n\n` +
              'That is the first line only. Read the rest with read_session ' +
              `on that session id.${run ? ` Decide it with that run id.` : ''}` +
              `${seen || twice}`,
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === COMMENT_REPLIED_EXTENSION
    ) {
      // RFC 0289. SYSTEM for the same reason as above: a remark made on a
      // document is not an instruction typed to the agent, and projecting it as
      // user text would have it obeyed rather than answered. The text names the
      // exact call that answers it, because replying in the thread is the whole
      // point: an answer typed into the transcript instead leaves the person
      // who asked looking at a comment nobody responded to.
      const d = p.payload.payload as {
        commentId?: unknown
        parentId?: unknown
        documentId?: unknown
        author?: unknown
        anchorText?: unknown
        nodeId?: unknown
        summary?: unknown
      }
      out.push({
        role: 'system',
        kind: 'comment_reply',
        // The PARENT, not the reply: it is the id the prose tells the agent to
        // answer, and the id a card must open to land in the same thread.
        ref: refOf({
          commentId: d.parentId,
          replyId: d.commentId,
          documentId: d.documentId,
          author: d.author,
          anchorText: d.anchorText,
          summary: d.summary,
        }),
        content: [
          {
            type: 'text',
            text:
              `${String(d.author ?? 'someone')} replied to a comment you wrote` +
              (d.documentId ? ` on document ${String(d.documentId)}` : '') +
              aboutClause(d.anchorText, d.nodeId) +
              `. They said: ${String(d.summary ?? '')}\n\n` +
              'That is the first line only. Read the thread with ' +
              `comment action:list${listHint(d.documentId, d.nodeId)}, and ` +
              'answer IN IT with comment ' +
              `action:reply id:${String(d.parentId ?? '')} — not here, ` +
              'or the person who asked never sees the answer. ' +
              COMMENT_ANSWER_RULE +
              // The reply's own id, not the one to answer: a pass marks read
              // the comment that was written, and that is this reply.
              ` Their reply's id, to pass with: ${String(d.commentId ?? '')}.`,
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === MENTIONED_EXTENSION
    ) {
      // RFC 0292. SYSTEM, for the third time and the same reason: being named
      // in a remark on a document is not an instruction typed to the agent, and
      // as user text it would be obeyed rather than answered. The text names the
      // call that answers it, because answering anywhere else leaves the person
      // who asked looking at a comment nobody responded to.
      const d = p.payload.payload as {
        commentId?: unknown
        documentId?: unknown
        author?: unknown
        anchorText?: unknown
        nodeId?: unknown
        summary?: unknown
      }
      out.push({
        role: 'system',
        kind: 'mentioned',
        ref: refOf({
          commentId: d.commentId,
          documentId: d.documentId,
          author: d.author,
          anchorText: d.anchorText,
          summary: d.summary,
        }),
        content: [
          {
            type: 'text',
            text:
              `${String(d.author ?? 'someone')} named you in a comment` +
              (d.documentId ? ` on document ${String(d.documentId)}` : '') +
              aboutClause(d.anchorText, d.nodeId) +
              `. They said: ${String(d.summary ?? '')}\n\n` +
              'That is the first line only. Read the thread with ' +
              `comment action:list${listHint(d.documentId, d.nodeId)}, and ` +
              'answer IN IT with comment ' +
              `action:reply id:${String(d.commentId ?? '')} — not here, ` +
              'or the person who named you never sees the answer. ' +
              COMMENT_ANSWER_RULE,
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === COMMENT_CLAIMED_EXTENSION
    ) {
      // RFC 0301 §3d. Nobody named this session: it is answering because it is
      // paired to the document and claimed the comment. The words say so,
      // because an answer that opens "you asked me" would be false, and they
      // allow pass, because a remark addressed to nobody may need no answer.
      const d = p.payload.payload as {
        commentId?: unknown
        documentId?: unknown
        author?: unknown
        anchorText?: unknown
        nodeId?: unknown
        summary?: unknown
      }
      out.push({
        role: 'system',
        kind: 'comment_claimed',
        ref: refOf({
          commentId: d.commentId,
          documentId: d.documentId,
          author: d.author,
          anchorText: d.anchorText,
          summary: d.summary,
        }),
        content: [
          {
            type: 'text',
            text:
              `${String(d.author ?? 'someone')} left a comment naming nobody ` +
              `on document ${String(d.documentId ?? '?')}, which you are ` +
              `paired to` +
              aboutClause(d.anchorText, d.nodeId) +
              `. They said: ${String(d.summary ?? '')}\n\n` +
              'You are the session answering it. Read the thread with ' +
              `comment action:list${listHint(d.documentId, d.nodeId)}, and ` +
              'answer IN IT with comment ' +
              `action:reply id:${String(d.commentId ?? '')}. ` +
              COMMENT_ANSWER_RULE,
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === EDITED_EXTENSION
    ) {
      // RFC 0301 S6. SYSTEM, for the reason every event here is: an edit is
      // something that happened, not an instruction. The words make passing
      // the ordinary answer, because most edits need nothing from a pair.
      const d = p.payload.payload as {
        documentId?: unknown
        diff?: unknown
        selectionLabel?: unknown
      }
      const selection = d.selectionLabel ? String(d.selectionLabel) : ''
      out.push({
        role: 'system',
        kind: 'edited',
        ref: refOf({ documentId: d.documentId }),
        content: [
          {
            type: 'text',
            text:
              `The person edited document ${String(d.documentId ?? '?')}, ` +
              'which you are paired to, and paused. What changed:\n\n' +
              `${String(d.diff ?? '')}\n\n` +
              (selection
                ? `They have this passage selected: “${selection}”\n\n`
                : '') +
              'Decide whether it needs you. If the edit is a note about the ' +
              'document itself (what it should say, rename, cut or fix), act ' +
              'on it: make the change, and say in one line what you changed. ' +
              'A write you may not make is refused, and then say what you ' +
              'would change. Say something if it needs words: a mistake, a ' +
              'contradiction, a question worth asking. Otherwise call pass ' +
              'and write nothing. Read the document first if the diff is not ' +
              'enough.',
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      p.payload.extensionType === UNDONE_EXTENSION
    ) {
      // RFC 0313 3f. SYSTEM: an undo is something that happened, not an
      // instruction. It starts no turn; the next turn reads it, and the words
      // are there so the agent does not make the same edit again.
      const d = p.payload.payload as {
        documentId?: unknown
        reverts?: unknown
        restores?: unknown
        byName?: unknown
        by?: unknown
        summary?: unknown
      }
      const who = String(d.byName ?? '') || 'The person'
      const restored = String(d.restores ?? '') !== ''
      out.push({
        role: 'system',
        kind: 'undone',
        ref: refOf({
          documentId: d.documentId,
          author: d.byName || d.by,
          summary: d.summary,
        }),
        content: [
          {
            type: 'text',
            text:
              `${who} ${restored ? 'restored an earlier version over' : 'undid'} ` +
              `your change to document ${String(d.documentId ?? '?')}: ` +
              `${String(d.summary ?? '')}\n\n` +
              'They chose to take it back. Do not make the same edit again ' +
              'unless they ask for it.',
          },
        ],
      })
    } else if (
      p.type === 'extension' &&
      (p.payload.extensionType === PAGE_TAKEN_OVER_EXTENSION ||
        p.payload.extensionType === PAGE_HANDED_BACK_EXTENSION)
    ) {
      // RFC 0303 3e. The person pressed Take over or Hand back on a web pane
      // this session was driving. Said in words the agent acts on: hands off
      // until it comes back, and carry on once it has.
      const url = String((p.payload.payload as { url?: unknown }).url ?? '?')
      const takenOver = p.payload.extensionType === PAGE_TAKEN_OVER_EXTENSION
      out.push({
        role: 'system',
        kind: takenOver ? 'page_taken_over' : 'page_handed_back',
        content: [
          {
            type: 'text',
            text: takenOver
              ? `The person took over ${url} in its pane. Do not act on ` +
                'that page until they hand it back.'
              : `The person handed ${url} back. You may act on it again; ` +
                'carry on with what you were doing.',
          },
        ],
      })
    } else if (p.type === 'worker_migration') {
      out.push({
        role: 'system',
        content: [
          {
            type: 'text',
            text: `The execution context changed (worker ${p.payload.fromWorkerId ?? '?'} -> ${p.payload.workerId}); prior filesystem state may be lost.`,
          },
        ],
      })
    }
    // run / inference_step / object_* / decision: not transcript.
  }

  return out
}
